/**
 * Ruleset registry — the frontend-side mirror of the `AtoRulesetVersion`
 * collection. Populated by hand at release time, then rendered under
 * Settings → "About / Tax rules" so tradies can see exactly what's in
 * force + when it was last reviewed.
 *
 * Immutability rules (enforced by code review, not by types):
 *  - Never mutate an existing entry.
 *  - New rulings ship as a new entry with a higher `version` string.
 *  - Exactly one entry has `isActive: true`.
 */

import { CURRENT_ATO_RULESET_VERSION } from "./current";

/**
 * The shape we lock the const literal against — plain strings, so the
 * literal type inference (`as const`) doesn't fight the branded
 * `IsoDateString` type. The registry rehydrates into full
 * `AtoRulesetVersion` at the seam (see `activeRuleset()`).
 */
interface AtoRulesetVersionEntry {
  readonly id: string;
  readonly version: string;
  readonly effectiveFrom: string;
  readonly lastReviewed: string;
  readonly notes: string;
  readonly isActive: boolean;
}

/**
 * Static reference table. Immutability lives in code review — never mutate
 * an existing entry, always append.
 */
export const ATO_RULESETS = [
  {
    id: "atorv-2026-1",
    version: "2026.1",
    effectiveFrom: "2025-07-01T00:00:00.000Z",
    lastReviewed: "2026-02-01T00:00:00.000Z",
    notes:
      "FY26 baseline. GST 10%, WFH fixed rate 70c/hr, cents-per-km 88c/km, IAWO $20,000, " +
      "GST registration threshold $75,000. Threshold constants match backend deps.py at cutover.",
    isActive: false,
  },
  {
    id: "atorv-2026-2",
    version: "2026.2",
    effectiveFrom: "2026-07-01T00:00:00.000Z",
    lastReviewed: "2026-08-31T00:00:00.000Z",
    notes:
      "FY27 refresh. Cents-per-km rises to 91c/km (PCG / Determination 2026); WFH held at 70c/hr; " +
      "super concessional cap $32,500; IAWO held at $20,000; GST 10% / registration $75k unchanged.",
    isActive: true,
  },
] as const satisfies ReadonlyArray<AtoRulesetVersionEntry>;

/** The single "in force" ruleset — used by the disclaimer stamper. */
export const activeRuleset =
  ATO_RULESETS.find((r) => r.version === CURRENT_ATO_RULESET_VERSION) ??
  ATO_RULESETS[0];

/** Convenience for admin UIs — list newest first. */
export const rulesetsNewestFirst = () =>
  [...ATO_RULESETS].sort((a, b) => (a.version < b.version ? 1 : -1));
